import { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../lib/utils';
import { Loader2 } from 'lucide-react';

type ButtonVariant = 'primary' | 'secondary' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface GlowButtonProps {
  children: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

interface Ripple {
  id: number;
  x: number;
  y: number;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-vokentra hover:bg-vokentra-dark text-white border-transparent shadow-lg shadow-vokentra/25',
  secondary:
    'bg-white/10 hover:bg-white/15 text-text-primary border-white/20 backdrop-blur-sm',
  ghost: 'bg-transparent hover:bg-white/5 text-text-secondary hover:text-text-primary border-transparent',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-2.5 text-sm',
  lg: 'px-8 py-3 text-base',
};

export function GlowButton({
  children,
  variant = 'primary',
  size = 'md',
  className,
  onClick,
  disabled = false,
  loading = false,
  icon,
  iconPosition = 'left',
  fullWidth = false,
  type = 'button',
}: GlowButtonProps) {
  const [ripples, setRipples] = useState<Ripple[]>([]);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const rippleIdRef = useRef(0);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled || loading) return;

      // Create ripple
      const rect = buttonRef.current?.getBoundingClientRect();
      if (rect) {
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const id = rippleIdRef.current++;
        setRipples((prev) => [...prev, { id, x, y }]);

        // Remove ripple after animation
        setTimeout(() => {
          setRipples((prev) => prev.filter((r) => r.id !== id));
        }, 600);
      }

      onClick?.();
    },
    [disabled, loading, onClick]
  );

  return (
    <motion.button
      ref={buttonRef}
      type={type}
      disabled={disabled || loading}
      onClick={handleClick}
      className={cn(
        'relative overflow-hidden rounded-xl font-medium transition-colors duration-200 border',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-vokentra/50',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'flex items-center justify-center gap-2',
        variantStyles[variant],
        sizeStyles[size],
        fullWidth && 'w-full',
        className
      )}
      whileHover={
        !disabled && !loading
          ? {
              scale: 1.02,
              transition: { duration: 0.2 },
            }
          : undefined
      }
      whileTap={
        !disabled && !loading
          ? {
              scale: 0.98,
              transition: { duration: 0.1 },
            }
          : undefined
      }
    >
      {/* Gradient border for primary */}
      {variant === 'primary' && (
        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-vokentra via-vokentra-light to-vokentra opacity-0 hover:opacity-100 transition-opacity duration-300 -z-10" />
      )}

      {/* Ripple effects */}
      <AnimatePresence>
        {ripples.map((ripple) => (
          <motion.span
            key={ripple.id}
            className="absolute bg-white/30 rounded-full pointer-events-none"
            style={{
              left: ripple.x,
              top: ripple.y,
              width: 10,
              height: 10,
              marginLeft: -5,
              marginTop: -5,
            }}
            initial={{ scale: 0, opacity: 0.6 }}
            animate={{ scale: 40, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          />
        ))}
      </AnimatePresence>

      {/* Loading spinner */}
      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div
            key="loader"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
          >
            <Loader2 className="w-4 h-4 animate-spin" />
          </motion.div>
        ) : (
          <>
            {icon && iconPosition === 'left' && (
              <motion.span
                key="icon-left"
                className="flex-shrink-0"
                initial={{ opacity: 0, x: -5 }}
                animate={{ opacity: 1, x: 0 }}
              >
                {icon}
              </motion.span>
            )}
            <motion.span
              key="children"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {children}
            </motion.span>
            {icon && iconPosition === 'right' && (
              <motion.span
                key="icon-right"
                className="flex-shrink-0"
                initial={{ opacity: 0, x: 5 }}
                animate={{ opacity: 1, x: 0 }}
              >
                {icon}
              </motion.span>
            )}
          </>
        )}
      </AnimatePresence>
    </motion.button>
  );
}

// Icon button variant
interface IconButtonProps {
  icon: React.ReactNode;
  onClick?: () => void;
  className?: string;
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  badge?: number;
  tooltip?: string;
}

export function IconButton({
  icon,
  onClick,
  className,
  variant = 'ghost',
  size = 'md',
  disabled = false,
  loading = false,
  badge,
}: IconButtonProps) {
  const sizeMap = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
  };

  const iconSizeMap = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  return (
    <motion.button
      onClick={onClick}
      disabled={disabled || loading}
      className={cn(
        'relative rounded-xl flex items-center justify-center transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-vokentra/50',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        sizeMap[size],
        variant === 'primary' && 'bg-vokentra hover:bg-vokentra-dark text-white',
        variant === 'secondary' && 'bg-white/10 hover:bg-white/15 text-text-primary',
        variant === 'ghost' && 'hover:bg-white/5 text-text-secondary hover:text-text-primary',
        className
      )}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      {loading ? (
        <Loader2 className={cn('animate-spin', iconSizeMap[size])} />
      ) : (
        <span className={iconSizeMap[size]}>{icon}</span>
      )}

      {/* Badge */}
      {badge !== undefined && badge > 0 && (
        <motion.span
          className="absolute -top-1 -right-1 w-5 h-5 bg-vokentra text-white text-[10px] font-bold rounded-full flex items-center justify-center"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 500, damping: 25 }}
        >
          {badge > 99 ? '99+' : badge}
        </motion.span>
      )}
    </motion.button>
  );
}
